(() => {
  if (customElements.get('byb-tier-check')) return;

  let playerPromise;
  const animationRequests = new Map();

  function loadPlayer(url) {
    if (window.lottie?.loadAnimation) return Promise.resolve(window.lottie);
    if (!playerPromise) {
      playerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => {
          if (window.lottie?.loadAnimation) resolve(window.lottie);
          else reject(new Error('Bundle animation player unavailable.'));
        };
        script.onerror = () => {
          script.remove();
          reject(new Error('Bundle animation player could not load.'));
        };
        document.head.appendChild(script);
      }).catch((error) => {
        playerPromise = null;
        throw error;
      });
    }
    return playerPromise;
  }

  function loadAnimationData(url) {
    if (!animationRequests.has(url)) {
      animationRequests.set(url, fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Bundle animation could not load.');
          return response.json();
        })
        .catch((error) => {
          animationRequests.delete(url);
          throw error;
        }));
    }
    return animationRequests.get(url);
  }

  function prepareAnimationData(data, color) {
    const copy = JSON.parse(JSON.stringify(data));
    if (!/^#[0-9a-f]{6}$/i.test(color || '')) return copy;
    const pink = [1, 3, 5].map((offset) => parseInt(color.slice(offset, offset + 2), 16) / 255);
    const recolorShapes = (shapes) => {
      shapes.forEach((shape) => {
        if (shape.it) recolorShapes(shape.it);
        const channels = shape.c?.k;
        if ((shape.ty === 'fl' || shape.ty === 'st') && shape.c?.a === 0 && Array.isArray(channels)) {
          const [red, green, blue] = channels;
          if (green > red * 1.2 && green > blue * 1.2) {
            shape.c.k = [...pink, channels[3] ?? 1];
          }
        }
      });
    };
    copy.layers?.forEach((layer) => recolorShapes(layer.shapes || []));
    return copy;
  }

  class BybTierCheck extends HTMLElement {
    static get observedAttributes() {
      return ['reached'];
    }

    connectedCallback() {
      if (this.motionPreference) return;
      this.generation = (this.generation || 0) + 1;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.sync();
      this.motionPreference.addEventListener('change', this.onMotionChange);
      this.sync();
    }

    disconnectedCallback() {
      this.generation += 1;
      this.motionPreference?.removeEventListener('change', this.onMotionChange);
      this.motionPreference = null;
      this.resetAnimation();
      this.loading = false;
    }

    attributeChangedCallback(name, previous, current) {
      if (previous !== current && this.motionPreference) this.sync();
    }

    resetAnimation() {
      this.ready = false;
      this.classList.remove('is-animation-ready');
      this.animation?.destroy();
      this.animation = null;
      this.replaceChildren();
    }

    sync() {
      if (!this.hasAttribute('reached')) {
        if (this.ready) this.animation.goToAndStop(0, true);
        return;
      }
      if (this.ready) {
        if (this.motionPreference.matches) {
          this.animation.goToAndStop(Math.max(0, this.animation.totalFrames - 1), true);
        } else {
          this.animation.goToAndPlay(0, true);
        }
      } else if (!this.loading && !this.motionPreference.matches) {
        this.initializeAnimation();
      }
    }

    async initializeAnimation() {
      const generation = this.generation;
      this.loading = true;
      try {
        const [player, data] = await Promise.all([
          loadPlayer(this.dataset.playerUrl),
          loadAnimationData(this.dataset.animationUrl)
        ]);
        if (!this.isConnected || generation !== this.generation) return;
        const container = document.createElement('span');
        container.className = 'free-bundle__tier-animation';
        this.appendChild(container);
        const animation = player.loadAnimation({
          container,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          animationData: prepareAnimationData(data, this.dataset.animationColor),
          rendererSettings: {
            viewBoxSize: '95 95 60 60',
            preserveAspectRatio: 'xMidYMid meet',
            progressiveLoad: false
          }
        });
        this.animation = animation;
        const fail = () => {
          if (generation !== this.generation) return;
          this.loading = false;
          this.resetAnimation();
        };
        const ready = () => {
          if (generation !== this.generation || this.ready) return;
          this.ready = true;
          this.loading = false;
          this.classList.add('is-animation-ready');
          this.sync();
        };
        animation.addEventListener('DOMLoaded', ready);
        animation.addEventListener('data_failed', fail);
        animation.addEventListener('error', fail);
        if (animation.isLoaded) ready();
      } catch (error) {
        if (generation !== this.generation) return;
        this.loading = false;
        this.resetAnimation();
      }
    }
  }

  customElements.define('byb-tier-check', BybTierCheck);
})();
